import { fromMarkdown } from 'mdast-util-from-markdown';
import type { Definition, Link, Nodes, Root } from 'mdast';
import { nodeText } from './conceptLinks';

type Citation = { article: string; act: string; url: string; names: string[] };
type Segment = { text: string; url?: string };

// Stable act identities, not edition dates. An abbreviation can only select
// a document already cited in this answer; it cannot create a source.
const ACT_NAMES: Record<string, string[]> = {
    '2013R0575': ['CRR'], '2013L0036': ['CRD'], '2016R0679': ['GDPR'],
    '2022R2554': ['DORA'], '2014L0059': ['BRRD'],
};

function citations(markdown: string): Citation[] {
    const root = fromMarkdown(markdown);
    const definitions = new Map(root.children.filter((n): n is Definition => n.type === 'definition').map(n => [n.identifier, n.url]));
    const found: Citation[] = [];
    const visit = (node: Nodes) => {
        const href = node.type === 'link' ? node.url : node.type === 'linkReference' ? definitions.get(node.identifier) : undefined;
        if (href) {
            try {
                const url = new URL(href);
                const act = /^CELEX:[03](\d{4}[RL]\d{4,5})(?:-\d{8})?$/.exec(url.searchParams.get('uri') || '')?.[1];
                if (url.protocol !== 'https:' || url.host !== 'eur-lex.europa.eu' || url.username || url.password || !act
                    || !/^\/legal-content\/EN\/TXT(?:\/HTML)?\/?$/.test(url.pathname)) return;
                const anchor = /^#art_(\d+[a-z]*)(?::~:|$)/i.exec(url.hash);
                // Title/lead text fragments have no article anchor. Reuse their
                // supplied URL only when its label identifies the article.
                const label = /\b(?:articles?|arts?\.?)\s+(\d+[a-z]*)\b/i.exec(nodeText(node));
                const article = anchor?.[1] || label?.[1];
                if (article) {
                    if (anchor) url.hash = `art_${article.toLowerCase()}`;
                    const names: string[] = [...(ACT_NAMES[act] || []), ...(nodeText(node).match(/\b[A-Z][A-Z\d]{1,9}\b/g) || [])];
                    const before = contentName(markdown.slice(0, node.position?.start.offset), true);
                    const after = contentName(markdown.slice(node.position?.end.offset), false);
                    if (before) names.push(before);
                    if (after) names.push(after);
                    found.push({ article: article.toLowerCase(), act, url: url.href,
                        names: names.filter(name => !['ARTICLE', 'ARTICLES', 'ART', 'ARTS', 'EU', 'EC'].includes(name)) });
                }
            } catch { /* Only source-backed EUR-Lex citations qualify. */ }
        } else if ('children' in node) node.children.forEach(visit);
    };
    visit(root);
    return found;
}

function contentName(text: string, before: boolean): string | undefined {
    return before
        ? /\b([A-Z][A-Z\d]{1,9})\s*$/.exec(text)?.[1]
        : /^\s+(?:(?:of|under|in)\s+(?:the\s+)?)?([A-Z][A-Z\d]{1,9})\b/.exec(text)?.[1];
}

/** Read destinations from the entire answer, including closed Read more
 * sections. Scope can narrow an ambiguous number to its cited act, but never
 * chooses between editions. Other articles in an unambiguous act/edition can
 * use its observed art_N anchor scheme; older title-only pages cannot. */
export function articleLinker(content: string, scope = content): (text: string) => Segment[] {
    const all = citations(content);
    const scopedActs = new Set(citations(scope).map(c => c.act));
    const destination = (article: string, name?: string): string | undefined => {
        const namedActs = new Set(all.filter(c => name && c.names.includes(name)).map(c => c.act));
        const acts = name ? namedActs : scopedActs;
        if (name && acts.size !== 1) return undefined;
        const sources = all.filter(c => acts.size !== 1 || acts.has(c.act));
        const candidates = sources.filter(c => c.article === article.toLowerCase());
        const urls = new Set(candidates.map(c => c.url));
        if (urls.size) return urls.size === 1 ? Array.from(urls)[0] : undefined;
        const documents = new Set(sources.map(c => c.url.split('#')[0]));
        if (documents.size !== 1 || !sources.some(c => /#art_\d+[a-z]*$/i.test(c.url))) return undefined;
        return `${Array.from(documents)[0]}#art_${article.toLowerCase()}`;
    };
    return (text: string) => {
        const parts: Segment[] = [];
        // Keep the full paragraph/point suffix clickable; plural lists link
        // each cited article separately ("Articles 89, 90 and 91").
        const mentions = /\b(?:articles?|arts?\.?)\s+\d+[a-z]*(?:\([\da-z]+\))*(?:(?:\s*[,–—-]\s*|\s+(?:and|or|to)\s+)\d+[a-z]*(?:\([\da-z]+\))*)*/gi;
        let start = 0;
        for (const mention of Array.from(text.matchAll(mentions))) {
            const offset = mention.index!;
            const before = contentName(text.slice(0, offset), true);
            const after = contentName(text.slice(offset + mention[0].length), false);
            const name = before || after;
            if (offset > start) parts.push({ text: text.slice(start, offset) });
            let itemStart = 0;
            const numbers = /\d+[a-z]*(?:\([\da-z]+\))*/gi;
            for (const number of Array.from(mention[0].matchAll(numbers))) {
                const numberStart = number.index!;
                if (itemStart && numberStart > itemStart) parts.push({ text: mention[0].slice(itemStart, numberStart) });
                const end = numberStart + number[0].length;
                parts.push({ text: mention[0].slice(itemStart ? numberStart : 0, end), url: destination(number[0].split('(')[0], name) });
                itemStart = end;
            }
            start = offset + mention[0].length;
        }
        if (start < text.length) parts.push({ text: text.slice(start) });
        return parts;
    };
}

export function remarkArticleLinks(link: (text: string) => Segment[]) {
    return () => (root: Root) => {
        const walk = (node: Nodes) => {
            if (node.type === 'link' || node.type === 'linkReference' || !('children' in node)) return;
            node.children = (node.children as Nodes[]).flatMap<Nodes>(child => {
                if (child.type !== 'text') { walk(child); return [child]; }
                return link(child.value).map(part => part.url ? {
                    type: 'link', url: part.url, children: [{ type: 'text', value: part.text }],
                } as Link : { type: 'text', value: part.text } as const);
            }) as typeof node.children;
        };
        walk(root);
    };
}
